import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "Shared/Button";
import styles from "../CartEditModal/CartEditModal.module.css";
import {
  resolveVariantIds,
  isVariantInList,
} from "utils/listVariantSelection";
import favoritesIcon from "assets/FavoritesHeader.svg";

const DEFAULT_SIZE_OPTIONS = [];

const DEFAULT_COLOR_OPTIONS = [];

// Map full size names to abbreviations
const SIZE_ABBREVIATIONS = {
  small: "S",
  medium: "M",
  large: "L",
  "x-large": "XL",
  "2x-large": "2X",
  "3x-large": "3X",
};

// Define size order from smallest to largest
const SIZE_ORDER = [
  "Small",
  "Medium",
  "Large",
  "X-Large",
  "2X-Large",
  "3X-Large",
];

function getSizeAbbreviation(sizeLabel) {
  if (!sizeLabel) return sizeLabel;
  const normalized = String(sizeLabel).toLowerCase().trim();
  return SIZE_ABBREVIATIONS[normalized] || sizeLabel;
}

function getSizeOrder(sizeLabel) {
  const index = SIZE_ORDER.findIndex(
    (size) => size.toLowerCase() === String(sizeLabel).toLowerCase()
  );
  return index >= 0 ? index : SIZE_ORDER.length;
}

export function FavoritesAddModal({
  isOpen,
  onClose,
  onSave,
  onCancel,
  item,
  sizeOptions,
  colorOptions,
  existingItems,
}) {
  const resolvedSizes = sizeOptions ?? DEFAULT_SIZE_OPTIONS;
  const resolvedColors = colorOptions ?? DEFAULT_COLOR_OPTIONS;
  const { t } = useTranslation();

  const [size, setSize] = useState(item?.size ?? null);
  const [colorIdx, setColorIdx] = useState(0);

  useEffect(() => {
    if (item) {
      setColorIdx(0);
      setSize(item.size ?? null);
    }
  }, [item]);

  // Build color→sizes and size→colors maps from variantMatrix for filtering
  const variantFilterMaps = useMemo(() => {
    // For filtering logic, we need to know which colors exist for each size and vice versa
    // The variantMatrix keys are in format "color::size", so we can parse them directly
    if (!item?.variantMatrix || Object.keys(item.variantMatrix).length === 0) {
      return { colorToSizes: new Map(), sizeToColors: new Map() };
    }

    const colorToSizes = new Map();
    const sizeToColors = new Map();

    // Parse the variantMatrix keys which are in format "color::size"
    Object.keys(item.variantMatrix).forEach((key) => {
      const [color, size] = key.split("::");

      if (color && size) {
        // Map color → set of sizes
        if (!colorToSizes.has(color)) {
          colorToSizes.set(color, new Set());
        }
        colorToSizes.get(color).add(size);

        // Map size → set of colors
        if (!sizeToColors.has(size)) {
          sizeToColors.set(size, new Set());
        }
        sizeToColors.get(size).add(color);
      }
    });

    return { colorToSizes, sizeToColors };
  }, [item?.variantMatrix]);

  // Filter available colors based on currently selected size
  const availableColors = useMemo(() => {
    if (!size || variantFilterMaps.sizeToColors.size === 0) {
      return resolvedColors;
    }

    const colorsForSize = variantFilterMaps.sizeToColors.get(size);
    if (!colorsForSize) {
      return resolvedColors;
    }

    return resolvedColors.filter((colorOption) =>
      colorsForSize.has(colorOption.label)
    );
  }, [size, resolvedColors, variantFilterMaps]);

  // Filter available sizes based on currently selected color
  const availableSizes = useMemo(() => {
    const currentColor = resolvedColors[colorIdx]?.label;
    if (!currentColor || variantFilterMaps.colorToSizes.size === 0) {
      return resolvedSizes;
    }

    const sizesForColor = variantFilterMaps.colorToSizes.get(currentColor);
    if (!sizesForColor) {
      return resolvedSizes;
    }

    return resolvedSizes.filter((sizeOption) =>
      sizesForColor.has(sizeOption.label)
    );
  }, [colorIdx, resolvedColors, resolvedSizes, variantFilterMaps]);

  const selectedIds = useMemo(() => {
    const selectedColorOption = resolvedColors[colorIdx] ?? {};
    const selectedSizeOption =
      resolvedSizes.find((option) => option?.label === size) ?? {};

    return resolveVariantIds({
      item,
      variantMatrix: item?.variantMatrix,
      selectedColorOption,
      selectedSizeOption,
      selectedColor: selectedColorOption?.label ?? "",
      selectedSize: size ?? "",
    });
  }, [item, resolvedColors, resolvedSizes, colorIdx, size]);

  const isAlreadyInList = useMemo(
    () =>
      isVariantInList(
        existingItems,
        item?.stockId,
        selectedIds.variationId,
        selectedIds.variantId
      ),
    [existingItems, item?.stockId, selectedIds]
  );

  const handleSave = () => {
    if (isAlreadyInList) return;

    const resolvedVariantId = selectedIds.variantId;
    const resolvedVariationId = selectedIds.variationId;
    onSave?.({
      ...item,
      color: resolvedColors[colorIdx]?.label,
      size,
      variationId: resolvedVariationId,
      variantId: resolvedVariantId,
    });
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      className={styles.modal}
      onClose={onClose}
      centered={true}
    >
      <div className={styles.iconCircleWrap}>
        <div className={styles.iconCircle}>
          <img
            src={favoritesIcon}
            alt="favorites"
            className={styles.cartIcon}
          />
        </div>
      </div>

      <div className={styles.header}>
        <span className={styles.headerTitle}>
          {t("favoritesAddModal.addToFavorites", "Add To Favorites")}
        </span>
      </div>

      {item?.isPreOrder ? (
        <div className={styles.preOrderBanner}>
          {t("cartEditModal.preOrder", "THIS ITEM IS AVAILABLE TO PRE-ORDER")}
        </div>
      ) : (
        <div className={styles.subheading}>
          {t("cartEditModal.pleaseSelect", "PLEASE SELECT THE FOLLOWING")}
        </div>
      )}

      <div className={styles.body}>
        {item?.color != null && availableColors.length > 0 && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>
              {t("cartEditModal.color", "Color")}
            </div>
            <div className={styles.thumbGrid}>
              {availableColors.map((c, i) => {
                // Find actual index in resolvedColors for selection state
                const actualIdx = resolvedColors.findIndex(
                  (col) => col.label === c.label
                );
                const selected = actualIdx === colorIdx;
                // Only use the specific color variant image, don't fall back to main image
                const imgSrc = c.imageUrl ?? null;
                return (
                  <button
                    key={`${c.label}-${i}`}
                    type="button"
                    className={`${styles.thumb} ${
                      selected ? styles.thumbSelected : ""
                    }`}
                    onClick={() => setColorIdx(actualIdx)}
                    aria-label={c.label}
                  >
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={c.label}
                        className={styles.thumbImg}
                      />
                    ) : (
                      <div
                        className={styles.thumbImg}
                        style={{ background: "#e0e5e8" }}
                      />
                    )}
                    <div className={styles.thumbLabel}>{c.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(item?.color == null || resolvedColors.length === 0) &&
          item?.image && (
            <div className={styles.field}>
              <div className={styles.bookImageWrap}>
                <img
                  src={item.image}
                  alt={item.title}
                  className={styles.bookImage}
                />
              </div>
            </div>
          )}

        {item?.size != null && availableSizes.length > 0 && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>
              {t("cartEditModal.size", "Size")}
            </div>
            <div className={styles.sizeRow}>
              {[...availableSizes]
                .sort((a, b) => getSizeOrder(a.label) - getSizeOrder(b.label))
                .map((sizeOption) => {
                  const isSelected = size === sizeOption.label;
                  const isSoldOut = sizeOption.isSoldOut;

                  return (
                    <button
                      key={sizeOption.label}
                      type="button"
                      className={`${styles.sizePill} ${
                        isSelected ? styles.sizeSelected : ""
                      } ${isSoldOut ? styles.sizeSoldOut : ""} ${isSoldOut && isSelected ? styles.sizeSelectedSoldOut : ""}`}
                      onClick={() => setSize(sizeOption.label)}
                    >
                      <span className={styles.sizePillText}>
                        {getSizeAbbreviation(sizeOption.label)}
                      </span>
                      {isSoldOut && (
                        <span className={styles.sizeSoldOutMark} aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          text={t("cartEditModal.cancel", "Cancel")}
          onClick={handleCancel}
          className="deleteBtn"
        />
        <Button
          text={
            isAlreadyInList
              ? t("addToListModal.alreadyInListButton", "Already in List")
              : t("addToListModal.addToList", "Add To List")
          }
          onClick={handleSave}
          className={isAlreadyInList ? "disabledBtn" : "confirmBtn"}
          disabled={isAlreadyInList}
        />
      </div>
    </Modal>
  );
}

FavoritesAddModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  sizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      imageUrl: PropTypes.string,
      variantId: PropTypes.string,
      isSoldOut: PropTypes.bool,
    })
  ),
  colorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      imageUrl: PropTypes.string,
      variantId: PropTypes.string,
    })
  ),
  existingItems: PropTypes.arrayOf(PropTypes.object),
  item: PropTypes.shape({
    stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variationId: PropTypes.string,
    variantId: PropTypes.string,
    size: PropTypes.string,
    color: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
    isPreOrder: PropTypes.bool,
    variantMatrix: PropTypes.object,
  }),
};

export default FavoritesAddModal;
